import {
  createContext,
  use,
  useId,
  useLayoutEffect,
  useSyncExternalStore,
  type RefObject,
} from "react"

/*
 * Where a page's bars dock on `compact`: at the foot of the screen, one above another, so a page
 * with two forms (Project settings' General and Instructions, Jira's projects) shows a SaveBar for
 * each form holding changes, and none hides another, and a ticket's comment bar keeps a SaveBar
 * above it. A change request's ActionBar and its chat box dock here too. The first to show sits
 * lowest. The page's scroll container keeps one dock
 * (`PageScroll`) and pads its foot by how tall the docked bars stand together. A bar outside any
 * dock docks alone at the foot.
 */

export type SaveBarDock = {
  /** Docks bar `id` above the bars already docked, and gives back what undocks it. */
  dock: (id: string, height: number) => () => void
  /** Records how tall bar `id` now stands, in px. */
  resize: (id: string, height: number) => void
  /** How far above the foot bar `id` docks, in px: the heights of the bars docked before it. */
  below: (id: string) => number
  /** How tall the docked bars stand together, in px. */
  height: () => number
  subscribe: (changed: () => void) => () => void
}

type Docked = { id: string; height: number }

/** A dock with nothing in it. */
export function dockOf(): SaveBarDock {
  let bars: readonly Docked[] = []
  const listeners = new Set<() => void>()
  const set = (next: readonly Docked[]) => {
    bars = next
    for (const changed of listeners) changed()
  }

  return {
    dock: (id, height) => {
      set([...bars, { id, height }])
      return () => set(bars.filter((bar) => bar.id !== id))
    },
    resize: (id, height) => {
      if (bars.some((bar) => bar.id === id && bar.height !== height))
        set(bars.map((bar) => (bar.id === id ? { id, height } : bar)))
    },
    below: (id) => {
      const at = bars.findIndex((bar) => bar.id === id)
      return bars.slice(0, Math.max(at, 0)).reduce((sum, bar) => sum + bar.height, 0)
    },
    height: () => bars.reduce((sum, bar) => sum + bar.height, 0),
    subscribe: (changed) => {
      listeners.add(changed)
      return () => void listeners.delete(changed)
    },
  }
}

// How far above a docked bar the line being typed on comes to rest, in px.
const clearOfBar = 8

/**
 * How far to scroll the page so `target` (the line being typed on, or the field when the caret
 * can't be found in it) stands 8px clear of a docked bar whose top is at `barTop`, never lifting
 * `target`'s top above `chromeBottom`, the foot of the chrome stuck to the page's top: a target
 * taller than the room between them keeps its top in view. A target the bar doesn't cover takes
 * none. Every edge is in the viewport's px.
 */
export function scrollToClear({
  target,
  barTop,
  chromeBottom,
}: {
  target: { top: number; bottom: number }
  barTop: number
  chromeBottom: number
}) {
  const covered = target.bottom + clearOfBar - barTop
  if (covered <= 0) return 0
  return Math.min(covered, Math.max(target.top - chromeBottom, 0))
}

/**
 * Where the Member is typing in `field`: the line the caret is on, when the selection is a caret in
 * its text (a Markdown editor's), or else the whole field (an input's or textarea's caret is not in
 * the document's selection).
 */
function typingAt(field: HTMLElement) {
  const selection = getSelection()
  if (selection?.rangeCount && selection.isCollapsed && field.contains(selection.anchorNode)) {
    const range = selection.getRangeAt(0)
    const caret = range.getBoundingClientRect()
    if (caret.height > 0) return caret
    // A caret on an empty line has no box of its own, so the line it is on stands in for it.
    const { startContainer } = range
    const line = startContainer instanceof Element ? startContainer : startContainer.parentElement
    if (line && field.contains(line)) return line.getBoundingClientRect()
  }
  return field.getBoundingClientRect()
}

/**
 * Scrolls the page until where the Member types in the field focused in `form` stands clear of
 * `bar` above it, never taking the field's top under the `stuck` px of chrome at the page's top.
 */
export function scrollFieldClear(bar: HTMLElement | null, form: Element | null, stuck: number) {
  const field = document.activeElement
  if (!bar || !(field instanceof HTMLElement) || !form?.contains(field) || bar.contains(field))
    return
  const scroller = bar.closest<HTMLElement>("[data-slot=page-scroll]")
  if (!scroller) return
  const by = scrollToClear({
    target: typingAt(field),
    barTop: bar.getBoundingClientRect().top,
    chromeBottom: scroller.getBoundingClientRect().top + stuck,
  })
  if (by > 0) scroller.scrollBy({ top: by })
}

/** The dock the bars inside share: the page's, or a specimen's own. */
export const SaveBarDockContext = createContext<SaveBarDock | null>(null)

const never = () => () => undefined

/**
 * Docks the bar `bar` holds in the dock it sits in while `docked` (on `compact`), keeping its
 * height current there as it grows and shrinks, and gives back how far above the foot it stands,
 * in px: the heights of the bars docked before it, as its bottom margin.
 */
export function useDockedBar(bar: RefObject<HTMLElement | null>, docked: boolean) {
  const id = useId()
  const pageDock = use(SaveBarDockContext)
  const dock = docked ? pageDock : null
  useLayoutEffect(() => {
    const element = bar.current
    if (!dock || !element) return
    const undock = dock.dock(id, element.offsetHeight)
    const observer = new ResizeObserver(() => dock.resize(id, element.offsetHeight))
    observer.observe(element)
    return () => {
      observer.disconnect()
      undock()
    }
  }, [bar, dock, id])
  return useSyncExternalStore(dock?.subscribe ?? never, () => dock?.below(id) ?? 0)
}
